import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  carregarPainelDoCliente,
  carregarPainelDoParticipante,
  type PainelDoCliente,
  type PainelDoParticipante,
} from '../servicos/painel';
import {
  carregarPerfilOperacional,
  type DashboardIcms,
  type PerfilOperacional,
} from '../servicos/perfil';
import { useEmpresaCorrente } from './useSessao';

/**
 * Consultas do painel da empresa corrente.
 *
 * **A empresa entra em toda `queryKey`** (RN-603): sem ela, trocar de vínculo
 * reusaria o cache do anterior e o usuário veria os números errados com o nome
 * certo — o efeito do `business[0]` do legado, só que mais difícil de notar.
 *
 * O `enabled` existe pelo mesmo motivo em todas: a guarda de rota já impede
 * chegar aqui sem empresa, mas uma consulta não deve depender de como a tela foi
 * roteada para não disparar com escopo indefinido.
 */

/** Qual dos três dashboards abrir — `servicos/perfil.ts`. */
export function usePerfilOperacional(): UseQueryResult<PerfilOperacional, Error> {
  const empresa = useEmpresaCorrente();

  return useQuery({
    queryKey: ['perfil-icms', empresa],
    enabled: empresa !== null,
    queryFn: ({ signal }) => carregarPerfilOperacional(empresa ?? '', signal),
  });
}

export function usePainelDoCliente(ano: number): UseQueryResult<PainelDoCliente, Error> {
  const empresa = useEmpresaCorrente();

  return useQuery({
    queryKey: ['painel', 'CLIENTE', empresa, ano],
    enabled: empresa !== null,
    queryFn: ({ signal }) => carregarPainelDoCliente(empresa ?? '', ano, signal),
  });
}

export function usePainelDoParticipante(
  dashboard: Exclude<DashboardIcms, 'CLIENTE'>,
  ano: number,
): UseQueryResult<PainelDoParticipante, Error> {
  const empresa = useEmpresaCorrente();

  return useQuery({
    // O dashboard entra na chave junto com a empresa: a mesma empresa pode ser
    // assessora e fornecedora (§2.1), e as duas respostas têm o mesmo formato —
    // sem isto, alternar entre as abas serviria o cache da outra.
    queryKey: ['painel', dashboard, empresa, ano],
    enabled: empresa !== null,
    queryFn: ({ signal }) => carregarPainelDoParticipante(dashboard, empresa ?? '', ano, signal),
  });
}
